// =====================================================
// PÁGINA: AdminCupons - Gerenciamento de Cupons
// =====================================================

import { useState, useEffect } from 'react'
import {
    Plus,
    Edit3,
    Trash2,
    Copy,
    Percent,
    Clock,
    Tag,
    CheckCircle,
    XCircle,
    AlertTriangle
} from 'lucide-react'
import { supabase } from '../services/supabase'
import { useToast } from '../hooks/useToast'

interface Cupom {
    id: string
    codigo: string
    nome: string
    descricao: string
    tipo: 'desconto_percentual' | 'desconto_fixo' | 'trial_gratis' | 'preco_fixo'
    valor: number // percentual, valor fixo, dias gratuitos, ou preço fixo
    plano_aplicavel: string // 'start', 'medium', 'pro' ou 'todos'
    data_inicio: string
    data_fim: string
    limite_uso: number | null
    vezes_usado: number
    ultimo_uso: string | null
    ativo: boolean
    created_at: string
    updated_at: string
}

interface CupomFormData {
    codigo: string
    nome: string
    descricao: string
    tipo: 'desconto_percentual' | 'desconto_fixo' | 'trial_gratis' | 'preco_fixo'
    valor: number
    plano_aplicavel: string
    data_inicio: string
    data_fim: string
    limite_uso: number | null
    ativo: boolean
}

export default function AdminCupons() {
    const [cupons, setCupons] = useState<Cupom[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string>('')
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [editingCupom, setEditingCupom] = useState<Cupom | null>(null)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [cupomToDelete, setCupomToDelete] = useState<Cupom | null>(null)
    const { showSuccess, showError } = useToast()

    const [formData, setFormData] = useState<CupomFormData>({
        codigo: '',
        nome: '',
        descricao: '',
        tipo: 'desconto_percentual',
        valor: 0,
        plano_aplicavel: 'todos',
        data_inicio: '',
        data_fim: '',
        limite_uso: null,
        ativo: true
    })

    useEffect(() => {
        loadCupons()
    }, [])

    // Recarregar cupons quando a página receber foco (para atualizar contadores)
    useEffect(() => {
        const handleFocus = () => {
            loadCupons()
        }

        window.addEventListener('focus', handleFocus)
        return () => window.removeEventListener('focus', handleFocus)
    }, [])

    const loadCupons = async () => {
        try {
            setLoading(true)
            setError('')

            // Verificar se o usuário está autenticado
            const { data: { user }, error: authError } = await supabase.auth.getUser()

            if (authError) {
                console.error('❌ Erro de autenticação:', authError)
                throw new Error('Usuário não autenticado')
            }

            if (!user) {
                console.error('❌ Nenhum usuário logado')
                throw new Error('Usuário não logado')
            }

            // Usar função SQL para contornar RLS
            const { data, error } = await supabase
                .rpc('buscar_cupons')

            if (error) {
                console.error('❌ Erro do Supabase:', error)
                throw error
            }

            setCupons(data || [])
        } catch (err) {
            console.error('❌ Erro ao carregar cupons:', err)
            const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido'
            setError(`Erro ao carregar cupons: ${errorMessage}`)
            showError(`Erro ao carregar cupons: ${errorMessage}`)
        } finally {
            setLoading(false)
        }
    }

    const generateCupomCode = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
        let result = ''
        for (let i = 0; i < 8; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length))
        }
        return result
    }

    const openModal = (cupom?: Cupom) => {
        if (cupom) {
            setEditingCupom(cupom)
            setFormData({
                codigo: cupom.codigo,
                nome: cupom.nome,
                descricao: cupom.descricao,
                tipo: cupom.tipo,
                valor: cupom.valor,
                plano_aplicavel: cupom.plano_aplicavel,
                data_inicio: cupom.data_inicio.split('T')[0],
                data_fim: cupom.data_fim.split('T')[0],
                limite_uso: cupom.limite_uso,
                ativo: cupom.ativo
            })
        } else {
            setEditingCupom(null)
            setFormData({
                codigo: generateCupomCode(),
                nome: '',
                descricao: '',
                tipo: 'desconto_percentual',
                valor: 0,
                plano_aplicavel: 'todos',
                data_inicio: new Date().toISOString().split('T')[0],
                data_fim: '',
                limite_uso: null,
                ativo: true
            })
        }
        setIsModalOpen(true)
    }

    const closeModal = () => {
        setIsModalOpen(false)
        setEditingCupom(null)
        setFormData({
            codigo: '',
            nome: '',
            descricao: '',
            tipo: 'desconto_percentual',
            valor: 0,
            plano_aplicavel: 'todos',
            data_inicio: '',
            data_fim: '',
            limite_uso: null,
            ativo: true
        })
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        try {
            // Validações
            if (!formData.codigo.trim()) {
                showError('Código do cupom é obrigatório')
                return
            }
            if (!formData.nome.trim()) {
                showError('Nome do cupom é obrigatório')
                return
            }
            if (!formData.data_inicio || !formData.data_fim) {
                showError('Datas de início e fim são obrigatórias')
                return
            }
            if (new Date(formData.data_inicio) >= new Date(formData.data_fim)) {
                showError('Data de fim deve ser posterior à data de início')
                return
            }

            const cupomData = {
                ...formData,
                data_inicio: new Date(formData.data_inicio).toISOString(),
                data_fim: new Date(formData.data_fim).toISOString(),
                codigo: formData.codigo.toUpperCase().trim()
            }

            if (editingCupom) {
                // Atualizar cupom existente usando função SQL
                const { error } = await supabase
                    .rpc('atualizar_cupom', {
                        p_id: editingCupom.id,
                        p_codigo: cupomData.codigo,
                        p_nome: cupomData.nome,
                        p_descricao: cupomData.descricao,
                        p_tipo: cupomData.tipo,
                        p_valor: cupomData.valor,
                        p_plano_aplicavel: cupomData.plano_aplicavel,
                        p_data_inicio: cupomData.data_inicio,
                        p_data_fim: cupomData.data_fim,
                        p_limite_uso: cupomData.limite_uso,
                        p_ativo: cupomData.ativo
                    })

                if (error) throw error

                showSuccess('Cupom atualizado com sucesso!')
            } else {
                // Criar novo cupom usando função SQL
                const { error } = await supabase
                    .rpc('inserir_cupom', {
                        p_codigo: cupomData.codigo,
                        p_nome: cupomData.nome,
                        p_descricao: cupomData.descricao,
                        p_tipo: cupomData.tipo,
                        p_valor: cupomData.valor,
                        p_plano_aplicavel: cupomData.plano_aplicavel,
                        p_data_inicio: cupomData.data_inicio,
                        p_data_fim: cupomData.data_fim,
                        p_limite_uso: cupomData.limite_uso,
                        p_ativo: cupomData.ativo
                    })

                if (error) throw error

                showSuccess('Cupom criado com sucesso!')
            }

            closeModal()
            loadCupons()
        } catch (err) {
            console.error('Erro ao salvar cupom:', err)
            showError('Erro ao salvar cupom')
        }
    }

    const handleDelete = (cupom: Cupom) => {
        setCupomToDelete(cupom)
        setIsDeleteModalOpen(true)
    }

    const confirmDelete = async () => {
        if (!cupomToDelete) return

        try {
            const { error } = await supabase
                .rpc('excluir_cupom', {
                    p_id: cupomToDelete.id
                })

            if (error) throw error

            showSuccess('Cupom excluído com sucesso!')
            setIsDeleteModalOpen(false)
            setCupomToDelete(null)
            loadCupons()
        } catch (err) {
            console.error('Erro ao excluir cupom:', err)
            showError('Erro ao excluir cupom')
        }
    }

    const copyCupomCode = (codigo: string) => {
        navigator.clipboard.writeText(codigo)
        showSuccess('Código copiado para a área de transferência!')
    }


    const getCupomStatus = (cupom: Cupom) => {
        const now = new Date()
        const inicio = new Date(cupom.data_inicio)
        const fim = new Date(cupom.data_fim)

        if (!cupom.ativo) {
            return { status: 'inativo', color: 'text-gray-500', icon: XCircle }
        }
        if (now < inicio) {
            return { status: 'aguardando', color: 'text-yellow-600', icon: Clock }
        }
        if (now > fim) {
            return { status: 'expirado', color: 'text-red-600', icon: XCircle }
        }
        if (cupom.limite_uso && cupom.vezes_usado >= cupom.limite_uso) {
            return { status: 'esgotado', color: 'text-red-600', icon: AlertTriangle }
        }
        return { status: 'ativo', color: 'text-green-600', icon: CheckCircle }
    }

    const formatCupomValue = (cupom: Cupom) => {
        switch (cupom.tipo) {
            case 'desconto_percentual':
                return `${cupom.valor}% de desconto`
            case 'desconto_fixo':
                return `R$ ${cupom.valor.toFixed(2)} de desconto`
            case 'trial_gratis':
                return `${cupom.valor} dias grátis`
            case 'preco_fixo':
                return `R$ ${cupom.valor.toFixed(2)}`
            default:
                return cupom.valor.toString()
        }
    }

    const getPlanoDisplay = (plano: string) => {
        switch (plano) {
            case 'start': return 'Start'
            case 'medium': return 'Medium'
            case 'pro': return 'Pro'
            case 'todos': return 'Todos os planos'
            default: return plano
        }
    }

    if (loading) {
        return (
            <div className="w-full">
                <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600 dark:text-gray-400">Carregando cupons...</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="w-full">
            <div className="w-full space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                            Gerenciar Cupons
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 mt-1">
                            Crie e gerencie cupons de desconto e promoções
                        </p>
                    </div>
                    <button
                        onClick={() => openModal()}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                    >
                        <Plus size={20} />
                        Novo Cupom
                    </button>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Total de Cupons</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                                    {cupons.length}
                                </p>
                            </div>
                            <Tag className="w-8 h-8 text-blue-600" />
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Cupons Ativos</p>
                                <p className="text-2xl font-bold text-green-600">
                                    {cupons.filter(c => getCupomStatus(c).status === 'ativo').length}
                                </p>
                            </div>
                            <CheckCircle className="w-8 h-8 text-green-600" />
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Cupons Expirados</p>
                                <p className="text-2xl font-bold text-red-600">
                                    {cupons.filter(c => getCupomStatus(c).status === 'expirado').length}
                                </p>
                            </div>
                            <XCircle className="w-8 h-8 text-red-600" />
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Total Usado</p>
                                <p className="text-2xl font-bold text-purple-600">
                                    {cupons.reduce((acc, c) => acc + (c.vezes_usado || 0), 0)}
                                </p>
                            </div>
                            <Percent className="w-8 h-8 text-purple-600" />
                        </div>
                    </div>
                </div>

                {/* Cupons List */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                            Lista de Cupons
                        </h2>

                        {error && (
                            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                <p className="text-red-600 dark:text-red-400">{error}</p>
                            </div>
                        )}

                        {cupons.length === 0 ? (
                            <div className="text-center py-8">
                                <Tag className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                                <p className="text-gray-500 dark:text-gray-400">Nenhum cupom encontrado</p>
                                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                                    Clique em "Novo Cupom" para criar o primeiro cupom
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-gray-200 dark:border-gray-700">
                                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                                                Código
                                            </th>
                                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                                                Nome
                                            </th>
                                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                                                Tipo
                                            </th>
                                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                                                Valor
                                            </th>
                                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                                                Plano
                                            </th>
                                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                                                Status
                                            </th>
                                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                                                Uso
                                            </th>
                                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                                                Ações
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cupons.map((cupom) => {
                                            const status = getCupomStatus(cupom)
                                            const StatusIcon = status.icon

                                            return (
                                                <tr key={cupom.id} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                                    <td className="py-3 px-4">
                                                        <div className="flex items-center gap-2">
                                                            <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-sm font-mono">
                                                                {cupom.codigo}
                                                            </code>
                                                            <button
                                                                onClick={() => copyCupomCode(cupom.codigo)}
                                                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                                                title="Copiar código"
                                                            >
                                                                <Copy size={14} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <div>
                                                            <p className="font-medium text-gray-900 dark:text-gray-100">
                                                                {cupom.nome}
                                                            </p>
                                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                                {cupom.descricao}
                                                            </p>
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                                                            {cupom.tipo.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <span className="font-medium text-gray-900 dark:text-gray-100">
                                                            {formatCupomValue(cupom)}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <span className="text-sm text-gray-600 dark:text-gray-400">
                                                            {getPlanoDisplay(cupom.plano_aplicavel)}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <div className="flex items-center gap-2">
                                                            <StatusIcon size={16} className={status.color} />
                                                            <span className={`text-sm font-medium ${status.color}`}>
                                                                {status.status.charAt(0).toUpperCase() + status.status.slice(1)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <span className="text-sm text-gray-600 dark:text-gray-400">
                                                            {cupom.vezes_usado || 0}
                                                            {cupom.limite_uso && ` / ${cupom.limite_uso}`}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => openModal(cupom)}
                                                                className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded transition-colors"
                                                                title="Editar"
                                                            >
                                                                <Edit3 size={16} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(cupom)}
                                                                className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                                                                title="Excluir"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal de Criação/Edição */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                                    {editingCupom ? 'Editar Cupom' : 'Novo Cupom'}
                                </h2>
                                <button
                                    onClick={closeModal}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    <XCircle size={24} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Código do Cupom *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.codigo}
                                            onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                                            placeholder="Ex: DESCONTO50"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Nome *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.nome}
                                            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                                            placeholder="Ex: Desconto de 50%"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Descrição
                                    </label>
                                    <textarea
                                        value={formData.descricao}
                                        onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                                        rows={3}
                                        placeholder="Descrição detalhada do cupom..."
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Tipo de Cupom *
                                        </label>
                                        <select
                                            value={formData.tipo}
                                            onChange={(e) => setFormData({ ...formData, tipo: e.target.value as any })}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                                            required
                                        >
                                            <option value="desconto_percentual">Desconto Percentual (%)</option>
                                            <option value="desconto_fixo">Desconto Fixo (R$)</option>
                                            <option value="trial_gratis">Trial Grátis (dias)</option>
                                            <option value="preco_fixo">Preço Fixo (R$)</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Valor *
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.valor}
                                            onChange={(e) => setFormData({ ...formData, valor: parseFloat(e.target.value) || 0 })}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                                            placeholder="0"
                                            step="0.01"
                                            min="0"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Plano Aplicável *
                                        </label>
                                        <select
                                            value={formData.plano_aplicavel}
                                            onChange={(e) => setFormData({ ...formData, plano_aplicavel: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                                            required
                                        >
                                            <option value="todos">Todos os planos</option>
                                            <option value="start">Start</option>
                                            <option value="medium">Medium</option>
                                            <option value="pro">Pro</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Limite de Uso
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.limite_uso || ''}
                                            onChange={(e) => setFormData({ ...formData, limite_uso: e.target.value ? parseInt(e.target.value) : null })}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                                            placeholder="Sem limite"
                                            min="1"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Data de Início *
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.data_inicio}
                                            onChange={(e) => setFormData({ ...formData, data_inicio: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Data de Fim *
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.data_fim}
                                            onChange={(e) => setFormData({ ...formData, data_fim: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="ativo"
                                        checked={formData.ativo}
                                        onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                                    />
                                    <label htmlFor="ativo" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                        Cupom ativo
                                    </label>
                                </div>

                                <div className="flex justify-end gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={closeModal}
                                        className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                                    >
                                        {editingCupom ? 'Atualizar' : 'Criar'} Cupom
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Confirmação de Exclusão */}
            {isDeleteModalOpen && cupomToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <AlertTriangle className="w-6 h-6 text-red-600" />
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                Confirmar Exclusão
                            </h3>
                        </div>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            Tem certeza que deseja excluir o cupom <strong>{cupomToDelete.nome}</strong>?
                            Esta ação não pode ser desfeita.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setIsDeleteModalOpen(false)
                                    setCupomToDelete(null)
                                }}
                                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                            >
                                Excluir
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
